import { useContext, useMemo } from 'react'
import { MoteurReservationPriceDisplayContext } from './MoteurReservationPriceDisplayContext'
import { convertWithRateMap, hasTndToPartnerRate } from '../utils/partnerRateMap'

/**
 * @param {null | {
 *   currency?: string,
 *   taux?: number | null,
 *   rateMap?: Map<string, number>,
 *   convertTnd: function,
 *   convertFrom?: function,
 *   displayToTnd: function
 * }} partnerDisplay
 */
export function usePartnerPriceDisplay(partnerDisplay) {
  const { priceDisplayMode } = useContext(MoteurReservationPriceDisplayContext)
  const canUsePartnerDevise = hasTndToPartnerRate(partnerDisplay)
  // Default / preferred: partner devise. Force TND when no TND→partner rate or user toggles TND.
  const showAsTnd = !!(
    partnerDisplay &&
    (!canUsePartnerDevise || priceDisplayMode === 'tnd')
  )

  return useMemo(
    () => ({
      priceDisplayMode,
      showAsTnd,
      canUsePartnerDevise,
      displayCurrency: !partnerDisplay
        ? ''
        : showAsTnd
          ? 'TND'
          : partnerDisplay.currency || 'TND',
      /**
       * Convert a TGX / moteur amount for UI.
       * TGX amounts are already in display currency (TND). Partner mode: TND → partner devise
       * via partner specification then global taux. No rate → keep TND.
       */
      convertForDisplay(amount, fromCurrency) {
        if (!partnerDisplay) return Number(Number(amount).toFixed(2))
        const from = String(fromCurrency || 'TND')
          .trim()
          .toUpperCase()
          .slice(0, 3)
        if (!from) return Number(Number(amount).toFixed(2))
        if (showAsTnd) {
          if (from === 'TND') return Number(Number(amount).toFixed(2))
          const mapped = convertWithRateMap(
            amount,
            from,
            'TND',
            partnerDisplay.rateMap
          )
          if (mapped != null) return mapped
          if (typeof partnerDisplay.convertFrom === 'function') {
            const inPartner = partnerDisplay.convertFrom(amount, from)
            if (inPartner == null || !Number.isFinite(Number(inPartner))) {
              return Number(Number(amount).toFixed(2))
            }
            const asTnd = partnerDisplay.displayToTnd(inPartner)
            if (asTnd == null || !Number.isFinite(Number(asTnd))) {
              return Number(Number(amount).toFixed(2))
            }
            return Number(Number(asTnd).toFixed(2))
          }
          return Number(Number(amount).toFixed(2))
        }
        const partnerCurrency = String(partnerDisplay.currency || '')
          .trim()
          .toUpperCase()
          .slice(0, 3)
        if (from && partnerCurrency && from === partnerCurrency) {
          return Number(Number(amount).toFixed(2))
        }
        if (from === 'TND') {
          if (!canUsePartnerDevise) return Number(Number(amount).toFixed(2))
          const converted =
            typeof partnerDisplay.convertTnd === 'function'
              ? partnerDisplay.convertTnd(amount)
              : convertWithRateMap(
                  amount,
                  'TND',
                  partnerCurrency,
                  partnerDisplay.rateMap
                )
          return converted == null || !Number.isFinite(Number(converted))
            ? Number(Number(amount).toFixed(2))
            : Number(Number(converted).toFixed(2))
        }
        if (typeof partnerDisplay.convertFrom === 'function') {
          const converted = partnerDisplay.convertFrom(amount, from)
          return converted == null || !Number.isFinite(Number(converted))
            ? Number(Number(amount).toFixed(2))
            : Number(Number(converted).toFixed(2))
        }
        const converted = partnerDisplay.convertTnd(amount)
        return converted == null || !Number.isFinite(Number(converted))
          ? Number(Number(amount).toFixed(2))
          : Number(Number(converted).toFixed(2))
      },
      displayInputToTnd(displayVal) {
        if (!partnerDisplay) return Number(Number(displayVal).toFixed(2))
        if (showAsTnd) return Number(Number(displayVal).toFixed(2))
        const asTnd = partnerDisplay.displayToTnd(displayVal)
        return asTnd == null || !Number.isFinite(Number(asTnd))
          ? Number(Number(displayVal).toFixed(2))
          : Number(Number(asTnd).toFixed(2))
      },
    }),
    [partnerDisplay, priceDisplayMode, showAsTnd, canUsePartnerDevise]
  )
}
